/** Business-object selection owned by one point feature, including deferred clusters. */
import { BehaviorSubject } from 'rxjs';

import { MapSelection } from './map-selection';
import type { MapTile } from './spatial/map-viewport';
import { latLngToTile } from './spatial/tile-math';

export interface PlaceClusterSelection extends MapTile {
  readonly featureKey: string;
  readonly count: number;
}

export interface PlaceClusterSelectionDecision extends PlaceClusterSelection {
  readonly selected: boolean;
}

interface SelectedPoint {
  readonly id: number;
  readonly lat: number;
  readonly lng: number;
}

/** Stable identity of one computed Place cluster. */
export type PlaceClusterId = string;

/**
 * Point-object selection identity: `objectId:clusterId`. Either side may be empty,
 * but not both. Cluster ids use `/`, so one split always recovers both parts.
 */
export type PointSelectionId = string;

export function placeClusterId(
  featureKey: string,
  tile: MapTile,
): PlaceClusterId {
  return `${featureKey}/${tile.zoom}/${tile.tileX}/${tile.tileY}`;
}

export function pointSelectionId(
  objectId: number | null,
  clusterId: PlaceClusterId | null,
): PointSelectionId {
  if (objectId === null && clusterId === null) {
    throw new Error('Object and cluster ids cannot both be null.');
  }
  return `${objectId ?? ''}:${clusterId ?? ''}`;
}

export function parsePointSelectionId(
  id: PointSelectionId,
): readonly [number | null, PlaceClusterId | null] {
  const [objectValue, clusterValue, extra] = id.split(':');
  if (extra !== undefined || (!objectValue && !clusterValue)) {
    throw new Error(`Invalid point selection id: ${id}`);
  }
  const objectId = objectValue ? Number(objectValue) : null;
  if (objectValue && !Number.isSafeInteger(objectId)) {
    throw new Error(`Invalid object id in selection id: ${id}`);
  }
  const clusterId = clusterValue || null;
  if (clusterId !== null) parsePlaceClusterId(clusterId);
  return [objectId, clusterId];
}

export function parsePlaceClusterId(
  id: PlaceClusterId,
): readonly [string, number, number, number] {
  const [featureKey, zoomValue, tileXValue, tileYValue, extra] = id.split('/');
  const zoom = Number(zoomValue);
  const tileX = Number(tileXValue);
  const tileY = Number(tileYValue);
  if (!featureKey || extra !== undefined
      || ![zoom, tileX, tileY].every(value =>
        Number.isSafeInteger(value) && value >= 0)) {
    throw new Error(`Invalid Place cluster id: ${id}`);
  }
  return [featureKey, zoom, tileX, tileY];
}

/** Complete view memento: ids alone lose excluded members and negative subtiles. */
export interface PointSelectionSnapshot {
  readonly ids: readonly number[];
  readonly excludedIds: readonly number[];
  readonly coordinates: readonly (readonly [number, { lat: number; lng: number }])[];
  readonly decisions: readonly PlaceClusterSelectionDecision[];
}

export class PointObjectSelection implements MapSelection<PointSelectionId> {
  private readonly _ids = new Set<number>();
  private readonly _excludedIds = new Set<number>();
  private readonly _coordinates = new Map<number, { lat: number; lng: number }>();
  private readonly _clusterDecisions =
    new Map<string, PlaceClusterSelectionDecision>();
  /** Lookup ceiling, updated on insert and reset when all decisions are cleared. */
  private maximumClusterZoom = 0;
  private cachedSize: number | undefined;

  private readonly changedSubject =
    new BehaviorSubject<readonly PointSelectionId[]>([]);
  readonly changed$ = this.changedSubject.asObservable();

  capture(): PointSelectionSnapshot {
    return structuredClone({ ids: [...this._ids], excludedIds: [...this._excludedIds],
      coordinates: [...this._coordinates], decisions: [...this._clusterDecisions.values()] });
  }

  restore(snapshot: PointSelectionSnapshot): void {
    const changed = new Set(this.ids);
    for (const id of this._excludedIds) changed.add(pointSelectionId(id, null));
    for (const id of this._clusterDecisions.keys()) changed.add(pointSelectionId(null, id));
    this._ids.clear();
    this._excludedIds.clear();
    this._coordinates.clear();
    this._clusterDecisions.clear();
    this.maximumClusterZoom = 0;
    for (const id of snapshot.ids) this._ids.add(id);
    for (const id of snapshot.excludedIds) {
      this._excludedIds.add(id);
      changed.add(pointSelectionId(id, null));
    }
    for (const [id, point] of snapshot.coordinates) this._coordinates.set(id, { ...point });
    for (const decision of snapshot.decisions) {
      const id = placeClusterId(decision.featureKey, decision);
      this._clusterDecisions.set(id, { ...decision });
      this.maximumClusterZoom = Math.max(this.maximumClusterZoom, decision.zoom);
      changed.add(pointSelectionId(null, id));
    }
    for (const id of this.ids) changed.add(id);
    this.cachedSize = undefined;
    this.publishIds(changed);
  }

  has(id: PointSelectionId): boolean {
    const [objectId, clusterId] = parsePointSelectionId(id);
    if (objectId !== null && !this._ids.has(objectId)) return false;
    if (clusterId !== null
        && !this._clusterDecisions.get(clusterId)?.selected) return false;
    return true;
  }
  hasObject(objectId: number): boolean { return this._ids.has(objectId); }
  get count(): number { return this.size; }
  get size(): number {
    if (this.cachedSize !== undefined) return this.cachedSize;
    let size = this._ids.size;
    for (const decision of this._clusterDecisions.values()) {
      const inherited = this.closestClusterDecision(
        decision.featureKey,
        decision,
        decision.zoom - 1,
      )?.selected ?? false;
      if (decision.selected === inherited) continue;
      size += decision.selected ? decision.count : -decision.count;
    }
    this.cachedSize = Math.max(0, size - this._excludedIds.size);
    return this.cachedSize;
  }
  get objectIds(): number[] { return [...this._ids]; }
  get ids(): PointSelectionId[] {
    return [
      ...[...this._ids].map(id => pointSelectionId(id, null)),
      ...[...this._clusterDecisions.entries()]
        .filter(([, decision]) => decision.selected)
        .map(([id]) => pointSelectionId(null, id)),
    ];
  }
  get clusters(): PlaceClusterSelection[] {
    return [...this._clusterDecisions.values()]
      .filter(decision => decision.selected)
      .map(decision => ({
        featureKey: decision.featureKey,
        zoom: decision.zoom,
        tileX: decision.tileX,
        tileY: decision.tileY,
        count: decision.count,
      }));
  }

  hasCluster(featureKey: string, tile: MapTile): boolean {
    return this._clusterDecisions.get(
      placeClusterId(featureKey, tile),
    )?.selected ?? false;
  }

  /**
   * Returns how many members of a rendered cluster are selected by deferred
   * cluster descriptors. A selected ancestor covers the whole rendered tile;
   * selected descendants contribute their recorded member counts.
   */
  selectedCountForCluster(
    featureKey: string,
    tile: MapTile,
    total: number,
  ): number {
    return this.selectedCountsForClusters(featureKey, [
      { ...tile, featureKey, count: total },
    ])[0];
  }

  /** A point inherits selection from any selected ancestor tile. */
  isPointCoveredByCluster(
    featureKey: string,
    lat: number,
    lng: number,
  ): boolean {
    return this.clusterSelectedAtPoint(featureKey, lat, lng);
  }

  toggleCluster(cluster: PlaceClusterSelection): void {
    const selected = this.selectedCountForCluster(
      cluster.featureKey,
      cluster,
      cluster.count,
    ) === cluster.count;
    if (this.setClusterSelected(cluster, !selected)) this.publish([], [cluster]);
  }

  addClusters(clusters: readonly PlaceClusterSelection[]): void {
    const changed: PlaceClusterSelection[] = [];
    for (const cluster of clusters) {
      if (this.selectedCountForCluster(
        cluster.featureKey,
        cluster,
        cluster.count,
      ) === cluster.count) continue;
      if (this.setClusterSelected(cluster, true)) changed.push(cluster);
    }
    this.publish([], changed);
  }

  isObjectSelected(
    featureKey: string,
    objectId: number,
    lat: number,
    lng: number,
  ): boolean {
    if (this._ids.has(objectId)) return true;
    if (this._excludedIds.has(objectId)) return false;
    return this.clusterSelectedAtPoint(featureKey, lat, lng);
  }

  toggleObject(
    featureKey: string,
    objectId: number,
    lat: number,
    lng: number,
  ): void {
    this.setObjectSelected(
      featureKey,
      objectId,
      lat,
      lng,
      !this.isObjectSelected(featureKey, objectId, lat, lng),
    );
  }

  setObjectSelected(
    featureKey: string,
    objectId: number,
    lat: number,
    lng: number,
    selected: boolean,
  ): void {
    this.setObjectsSelected(featureKey, [{ id: objectId, lat, lng }], selected);
  }

  /**
   * Computes deferred counts by assigning each decision's contribution to its
   * requested ancestor tiles once. Parent state uses ID lookups, not scans of
   * all decisions. The aggregate is local to this call, not selection state.
   */
  selectedCountsForClusters(
    featureKey: string,
    clusters: readonly PlaceClusterSelection[],
  ): readonly number[] {
    if (!clusters.length) return [];
    const zooms = [...new Set(clusters.map(cluster => cluster.zoom))];
    const deltasByTile = new Map(clusters.map(cluster => [
      tileKey(cluster.zoom, cluster.tileX, cluster.tileY), 0,
    ]));
    for (const decision of this._clusterDecisions.values()) {
      if (decision.featureKey !== featureKey) continue;
      const inherited = this.closestClusterDecision(
        featureKey,
        decision,
        decision.zoom - 1,
      )?.selected ?? false;
      if (decision.selected === inherited) continue;
      const delta = decision.selected ? decision.count : -decision.count;
      for (const zoom of zooms) {
        if (zoom >= decision.zoom) continue;
        const scale = 2 ** (decision.zoom - zoom);
        const key = tileKey(zoom,
          Math.floor(decision.tileX / scale), Math.floor(decision.tileY / scale));
        const current = deltasByTile.get(key);
        if (current !== undefined) deltasByTile.set(key, current + delta);
      }
    }
    for (const id of this._excludedIds) {
      const point = this._coordinates.get(id);
      if (!point || !this.clusterSelectedAtPoint(
        featureKey,
        point.lat,
        point.lng,
      )) continue;
      for (const zoom of zooms) {
        const tile = latLngToTile(point.lat, point.lng, zoom);
        const key = tileKey(zoom, tile.tileX, tile.tileY);
        const current = deltasByTile.get(key);
        if (current !== undefined) deltasByTile.set(key, current - 1);
      }
    }

    return clusters.map(cluster => {
      const ancestor = this.closestClusterDecision(featureKey, cluster);
      let selectedCount = ancestor?.selected ? cluster.count : 0;
      selectedCount += deltasByTile.get(tileKey(
        cluster.zoom,
        cluster.tileX,
        cluster.tileY,
      )) ?? 0;
      return Math.max(0, Math.min(selectedCount, cluster.count));
    });
  }

  /** Updates many resolved objects and publishes one render notification. */
  setObjectsSelected(
    featureKey: string,
    objects: readonly SelectedPoint[],
    selected: boolean,
  ): void {
    const changed = this.setObjectsSelectedWithoutPublishing(
      featureKey,
      objects,
      selected,
    );
    this.publish(changed);
  }

  /** Applies one rendered cluster hit as one selection transaction. */
  setObjectsAndClustersSelected(
    featureKey: string,
    objects: readonly SelectedPoint[],
    clusters: readonly PlaceClusterSelection[],
    selected: boolean,
  ): void {
    const changedIds = this.setObjectsSelectedWithoutPublishing(
      featureKey,
      objects,
      selected,
    );
    const changedClusters: PlaceClusterSelection[] = [];
    for (const cluster of clusters) {
      const selectedCount = this.selectedCountForCluster(
        cluster.featureKey,
        cluster,
        cluster.count,
      );
      // Zero deferred coverage can still contain directly selected objects.
      // Always apply removal so setClusterSelected clears those overrides too.
      if (selected && selectedCount === cluster.count) continue;
      if (this.setClusterSelected(cluster, selected)) {
        changedClusters.push(cluster);
      }
    }
    this.publish(changedIds, changedClusters);
  }

  /** Replaces the complete object selection as one published transaction. */
  replaceObjectsAndClusters(
    featureKey: string,
    objects: readonly SelectedPoint[],
    clusters: readonly PlaceClusterSelection[],
  ): void {
    const changed = new Set(this.ids);
    for (const objectId of this._excludedIds) {
      changed.add(pointSelectionId(objectId, null));
    }
    for (const clusterId of this._clusterDecisions.keys()) {
      changed.add(pointSelectionId(null, clusterId));
    }
    this._ids.clear();
    this._excludedIds.clear();
    this._coordinates.clear();
    this._clusterDecisions.clear();
    this.maximumClusterZoom = 0;

    for (const cluster of clusters) {
      this.setClusterSelected(cluster, true);
    }
    this.setObjectsSelectedWithoutPublishing(featureKey, objects, true);

    for (const id of this.ids) changed.add(id);
    this.publishIds(changed);
  }

  filterResolvedObjects(
    featureKey: string,
    objects: readonly SelectedPoint[],
  ): readonly SelectedPoint[] {
    return objects.filter(object => this.isObjectSelected(
      featureKey,
      object.id,
      object.lat,
      object.lng,
    ));
  }

  toggle(id: PointSelectionId): void {
    if (this.has(id)) this.remove([id]);
    else this.append([id]);
  }

  removeClusters(clusters: readonly PlaceClusterSelection[]): void {
    const changed: PlaceClusterSelection[] = [];
    for (const cluster of clusters) {
      if (this.selectedCountForCluster(
        cluster.featureKey,
        cluster,
        cluster.count,
      ) === 0) continue;
      if (this.setClusterSelected(cluster, false)) changed.push(cluster);
    }
    this.publish([], changed);
  }

  append(ids: readonly PointSelectionId[]): void {
    const changed = new Set<PointSelectionId>();
    for (const id of ids) {
      const [objectId, clusterId] = parsePointSelectionId(id);
      if (objectId !== null) {
        this._excludedIds.delete(objectId);
        if (!this._ids.has(objectId)) {
          this._ids.add(objectId);
          changed.add(id);
        }
      }
      if (clusterId !== null) {
        const cluster = this._clusterDecisions.get(clusterId);
        if (!cluster) {
          throw new Error(`Unknown Place cluster id: ${clusterId}`);
        }
        if (this.setClusterSelected(cluster, true)) changed.add(id);
      }
    }
    this.publishIds(changed);
  }

  replace(ids: readonly PointSelectionId[]): void {
    const previous = new Set(this.ids);
    const next = new Set(ids);
    if (previous.size === next.size
        && [...previous].every(id => next.has(id))) return;

    const directIds = new Set<number>();
    const clusters: PlaceClusterSelectionDecision[] = [];
    for (const id of next) {
      const [objectId, clusterId] = parsePointSelectionId(id);
      if (objectId !== null) directIds.add(objectId);
      if (clusterId === null) continue;
      const cluster = this._clusterDecisions.get(clusterId);
      if (!cluster) {
        throw new Error(`Unknown Place cluster id: ${clusterId}`);
      }
      clusters.push({ ...cluster, selected: true });
    }

    this._ids.clear();
    this._excludedIds.clear();
    this._coordinates.clear();
    this._clusterDecisions.clear();
    this.maximumClusterZoom = 0;
    for (const id of directIds) this._ids.add(id);
    for (const cluster of clusters) {
      this.maximumClusterZoom = Math.max(this.maximumClusterZoom, cluster.zoom);
      this._clusterDecisions.set(
        placeClusterId(cluster.featureKey, cluster),
        cluster,
      );
    }
    this.publishIds(new Set([...previous, ...next]));
  }

  remove(ids: readonly PointSelectionId[]): void {
    const changed = new Set<PointSelectionId>();
    for (const id of ids) {
      const [objectId, clusterId] = parsePointSelectionId(id);
      if (objectId !== null && this._ids.delete(objectId)) {
        this._excludedIds.delete(objectId);
        this._coordinates.delete(objectId);
        changed.add(id);
      }
      if (clusterId !== null) {
        const cluster = this._clusterDecisions.get(clusterId);
        if (cluster && this.setClusterSelected(cluster, false)) changed.add(id);
      }
    }
    this.publishIds(changed);
  }

  clear(): void {
    if (!this._ids.size && !this._excludedIds.size &&
        !this._clusterDecisions.size) return;
    const changed = this.ids;
    this._ids.clear();
    this._excludedIds.clear();
    this._coordinates.clear();
    this._clusterDecisions.clear();
    this.maximumClusterZoom = 0;
    this.publishIds(changed);
  }

  private setObjectsSelectedWithoutPublishing(
    featureKey: string,
    objects: readonly SelectedPoint[],
    selected: boolean,
  ): Set<number> {
    const changed = new Set<number>();
    for (const object of objects) {
      this._coordinates.set(object.id, {
        lat: object.lat,
        lng: object.lng,
      });
      const inherited = this.clusterSelectedAtPoint(
        featureKey,
        object.lat,
        object.lng,
      );
      const wasSelected = this.isObjectSelected(
        featureKey,
        object.id,
        object.lat,
        object.lng,
      );
      this._ids.delete(object.id);
      this._excludedIds.delete(object.id);
      if (selected !== inherited) {
        (selected ? this._ids : this._excludedIds).add(object.id);
      } else {
        this._coordinates.delete(object.id);
      }
      if (wasSelected !== selected) changed.add(object.id);
    }
    return changed;
  }

  private publish(
    changedobjects: Iterable<number>,
    changedClusters: Iterable<PlaceClusterSelection> = [],
  ): void {
    const ids = new Set<PointSelectionId>();
    for (const objectId of changedobjects) {
      ids.add(pointSelectionId(objectId, null));
    }
    for (const cluster of changedClusters) {
      ids.add(pointSelectionId(
        null,
        placeClusterId(cluster.featureKey, cluster),
      ));
    }
    this.publishIds(ids);
  }

  private publishIds(ids: Iterable<PointSelectionId>): void {
    const changed = [...ids];
    if (!changed.length) return;
    this.cachedSize = undefined;
    this.changedSubject.next(changed);
  }

  private clusterSelectedAtPoint(
    featureKey: string,
    lat: number,
    lng: number,
  ): boolean {
    if (!this._clusterDecisions.size) return false;
    const zoom = this.maximumClusterZoom;
    return this.closestClusterDecision(featureKey, {
      ...latLngToTile(lat, lng, zoom), zoom,
    })?.selected ?? false;
  }

  private closestClusterDecision(
    featureKey: string,
    tile: MapTile,
    maximumZoom = tile.zoom,
  ): PlaceClusterSelectionDecision | undefined {
    if (!this._clusterDecisions.size) return undefined;
    const startZoom = Math.min(maximumZoom, tile.zoom, this.maximumClusterZoom);
    const scale = 2 ** (tile.zoom - startZoom);
    let tileX = Math.floor(tile.tileX / scale);
    let tileY = Math.floor(tile.tileY / scale);
    for (let zoom = startZoom; zoom >= 0; zoom--) {
      const decision = this._clusterDecisions.get(
        placeClusterId(featureKey, { zoom, tileX, tileY }),
      );
      if (decision) return decision;
      tileX = Math.floor(tileX / 2);
      tileY = Math.floor(tileY / 2);
    }
    return undefined;
  }

  /**
   * Sets one rendered tile as a whole. More-specific cluster and marker
   * overrides inside the tile are discarded so a second click can always
   * reverse the first one, regardless of the current zoom level.
   */
  private setClusterSelected(
    cluster: PlaceClusterSelection,
    selected: boolean,
  ): boolean {
    let changed = false;
    for (const [decisionKey, decision] of this._clusterDecisions) {
      if (decision.featureKey !== cluster.featureKey ||
          !tileContains(cluster, decision)) continue;
      this._clusterDecisions.delete(decisionKey);
      changed = true;
    }

    for (const [objectId, point] of this._coordinates) {
      if (!pointInTile(point.lat, point.lng, cluster)) continue;
      changed = this._ids.delete(objectId) || changed;
      changed = this._excludedIds.delete(objectId) || changed;
    }

    const inherited = this.closestClusterDecision(
      cluster.featureKey,
      cluster,
      cluster.zoom - 1,
    )?.selected ?? false;
    if (selected !== inherited) {
      this.maximumClusterZoom = Math.max(this.maximumClusterZoom, cluster.zoom);
      this._clusterDecisions.set(
        placeClusterId(cluster.featureKey, cluster),
        { ...cluster, selected },
      );
      changed = true;
    }
    return changed;
  }
}

function tileKey(zoom: number, tileX: number, tileY: number): string {
  return `${zoom}/${tileX}/${tileY}`;
}

function tileContains(parent: MapTile, child: MapTile): boolean {
  if (parent.zoom > child.zoom) return false;
  const scale = 2 ** (child.zoom - parent.zoom);
  return Math.floor(child.tileX / scale) === parent.tileX &&
    Math.floor(child.tileY / scale) === parent.tileY;
}

function pointInTile(lat: number, lng: number, tile: MapTile): boolean {
  const pointTile = latLngToTile(lat, lng, tile.zoom);
  return pointTile.tileX === tile.tileX && pointTile.tileY === tile.tileY;
}
